import { useEffect, useState } from 'react';
import { Modal } from './Modal.jsx';
import { Icon } from './Icon.jsx';
import { useT } from '../i18n/index.js';
import { renderThumbnail } from '../utils/pdfRender.js';

// Page chooser used both for "merge with page selection" and for the
// "split / extract" screen. doc = a pdf.js document, count = page count.
export function PagePickerModal({
  open,
  doc,
  count,
  metaPages,
  title,
  confirmLabel,
  onConfirm,
  onClose,
}) {
  const t = useT();
  const [thumbs, setThumbs] = useState([]);
  const [picked, setPicked] = useState(() => new Set());

  useEffect(() => {
    if (!open || !doc) return;
    setPicked(new Set(Array.from({ length: count }, (_, i) => i)));
    setThumbs([]);
    let alive = true;
    (async () => {
      const out = [];
      for (let i = 0; i < count; i++) {
        // metaPages (when given) maps cell i to its source page +
        // rotation, so the picker matches the editor's current order.
        const origIdx = metaPages ? metaPages[i].originalIndex : i;
        const rot = metaPages ? metaPages[i].rotation : 0;
        try {
          const url = await renderThumbnail(doc, origIdx, 0.4, rot);
          if (!alive) return;
          out.push(url);
          setThumbs([...out]);
        } catch {
          out.push(null);
          setThumbs([...out]);
        }
      }
    })();
    return () => {
      alive = false;
    };
  }, [open, doc, count, metaPages]);

  const toggle = (i) => {
    const next = new Set(picked);
    next.has(i) ? next.delete(i) : next.add(i);
    setPicked(next);
  };
  const all = () => setPicked(new Set(Array.from({ length: count }, (_, i) => i)));
  const none = () => setPicked(new Set());

  const confirm = () => {
    const indices = [...picked].sort((a, b) => a - b);
    if (indices.length) onConfirm(indices);
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      size="lg"
      footer={
        <>
          <button className="btn sm" onClick={all}>{t('picker.all')}</button>
          <button className="btn sm ghost" onClick={none}>{t('picker.none')}</button>
          <span className="grow" />
          <span className="chip">{t('picker.count', { n: picked.size, t: count })}</span>
          <button className="btn primary sm" onClick={confirm} disabled={picked.size === 0}>
            <Icon name="check" size={14} /> {confirmLabel}
          </button>
        </>
      }
    >
      <div className="picker-grid">
        {Array.from({ length: count }, (_, i) => (
          <button
            key={i}
            className={`picker-cell ${picked.has(i) ? 'on' : ''}`}
            onClick={() => toggle(i)}
            aria-pressed={picked.has(i)}
          >
            <div className="picker-thumb">
              {thumbs[i] ? <img src={thumbs[i]} alt={`Page ${i + 1}`} /> : <div className="skeleton" />}
            </div>
            <div className="picker-cell-foot">
              <span className={`picker-check ${picked.has(i) ? 'on' : ''}`}>
                {picked.has(i) && <Icon name="check" size={12} />}
              </span>
              <span className="tiny">{i + 1}</span>
            </div>
          </button>
        ))}
      </div>
    </Modal>
  );
}
